import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  addToast,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { formatCurrency } from "../../../shared/utils/utils";
import { api } from "@/lib/api-client";
import dayjs from "dayjs";
import { CustomerSubscriptionInvoice } from "@/server-types";

const getInvoiceStatus = (invoice: CustomerSubscriptionInvoice) => {
  if (invoice.amountPaid >= invoice.amountDue) {
    return {
      value: "paid",
      text: t`Plačano`,
    };
  }
  if (invoice.amountPaid > 0) {
    return {
      value: "partial",
      text: t`Delno plačano`,
    };
  }
  return {
    value: "unpaid",
    text: t`Neplačano`,
  };
};

export const SubscriptionInvoiceModal = ({
  isOpen,
  onClose,
  customerSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}) => {
  const { data: invoices, refetch: refetchInvoices } =
    api.customer.useGetSubscriptionInvoices({
      customerSubscriptionId,
    });

  const updateInvoiceMutation = api.customer.useUpdateSubscriptionInvoice();

  const handleConfirmPayment = async (invoiceId: number, amountDue: number) => {
    try {
      await updateInvoiceMutation.mutateAsync({
        invoiceId,
        body: {
          amountPaid: amountDue,
        },
      });
      refetchInvoices();
      addToast({
        title: t`Uspešno potrjeno plačilo`,
        description: t`Plačilo je bilo uspešno zabeleženo`,
        color: "success",
      });
    } catch (error) {
      console.error("Failed to confirm payment:", error);
      addToast({
        title: t`Napaka pri potrditvi plačila`,
        description: t`Prišlo je do napake pri potrjevanju plačila. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const handleMarkAsUnpaid = async (invoiceId: number) => {
    try {
      await updateInvoiceMutation.mutateAsync({
        invoiceId,
        body: {
          amountPaid: 0,
        },
      });
      refetchInvoices();
      addToast({
        title: t`Uspešno označeno kot neplačano`,
        description: t`Račun je bil označen kot neplačan`,
        color: "success",
      });
    } catch (error) {
      console.error("Failed to mark as unpaid:", error);
      addToast({
        title: t`Napaka pri označevanju kot neplačano`,
        description: t`Prišlo je do napake. Poskusite ponovno.`,
        color: "danger",
      });
    }
  };

  const { locale } = i18n;

  if (!invoices) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <Trans>Plačila naročnine</Trans>
            </ModalHeader>
            <ModalBody>
              <Table aria-label="Invoices" removeWrapper>
                <TableHeader>
                  <TableColumn>
                    <Trans>Obdobje</Trans>
                  </TableColumn>
                  <TableColumn>
                    <Trans>Znesek</Trans>
                  </TableColumn>
                  <TableColumn>
                    <Trans>Status</Trans>
                  </TableColumn>
                  <TableColumn>
                    <Trans>Akcije</Trans>
                  </TableColumn>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const status = getInvoiceStatus(invoice);
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-small">
                              {dayjs(invoice.periodStart)
                                .toDate()
                                .toLocaleDateString(locale)}
                            </span>
                            <span className="text-tiny text-default-500">
                              <Trans>do</Trans>{" "}
                              {dayjs(invoice.periodEnd)
                                .toDate()
                                .toLocaleDateString(locale)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span>
                              {formatCurrency({
                                amount: invoice.amountDue / 100,
                                locale,
                                currency: "EUR",
                              })}
                            </span>
                            {status.value !== "unpaid" && (
                              <span className="text-tiny text-default-500">
                                <Trans>Plačano:</Trans>{" "}
                                {formatCurrency({
                                  amount: invoice.amountPaid / 100,
                                  locale,
                                  currency: "EUR",
                                })}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span>
                            {status.value === "paid" && <Trans>Plačano</Trans>}
                            {status.value === "unpaid" && (
                              <Trans>Neplačano</Trans>
                            )}
                            {status.value === "partial" && (
                              <Trans>Delno plačano</Trans>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          {status.value !== "paid" && (
                            <Button
                              size="sm"
                              isLoading={updateInvoiceMutation.isPending}
                              onPress={() =>
                                handleConfirmPayment(
                                  invoice.id,
                                  invoice.amountDue,
                                )
                              }
                            >
                              <Trans>Potrdi plačilo</Trans>
                            </Button>
                          )}
                          {status.value !== "unpaid" && (
                            <Button
                              size="sm"
                              color="danger"
                              isLoading={updateInvoiceMutation.isPending}
                              onPress={() => handleMarkAsUnpaid(invoice.id)}
                            >
                              <Trans>Označi kot neplačano</Trans>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ModalBody>
            <ModalFooter></ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
